import React from 'react';
import MainPrintLogo from './MainPrintLogo';
import { printPalette, printTypography, printInventory } from './printPalette';
import { sharedPrintStyles, basePrintContainerStyle } from './printStyles';

interface RelatedCarItem {
  chassisNo: string;
  make: string;
  model: string;
  relationshipType: string; // 'STC' or 'Attached'
}

interface CarItem {
  position: number;
  dealer: string;
  port: string;
  orderNo?: string;
  consignee: string;
  chassisNo: string;
  make: string;
  model: string;
  type: string;
  collect?: number | string;
  relatedCars?: RelatedCarItem[];
}

interface InventoryListProps {
  companyName?: string;
  companyAddress?: string;
  voyageNo: string;
  date: string;
  shipName: string;
  loadingPort: string;
  shipperName?: string;
  cars: CarItem[];
  title?: string;
  showCollect?: boolean;
  includeRelated?: boolean;
}

const InventoryList: React.FC<InventoryListProps> = ({
  companyName = 'CLAPOTIS CARRIERS LTD',
  companyAddress = 'Trust Company Complex, Ajeltake Road, Majuro, Marshall Islands',
  voyageNo,
  date,
  shipName,
  loadingPort,
  shipperName,
  cars,
  title = 'PARKING LIST',
  showCollect = false,
  includeRelated = true
}) => {
  const colWidthsMm = showCollect
    ? printInventory?.colWidthsCollectMm || [14, 18, 28, 20, 63, 36, 66, 18, 14]
    : printInventory?.colWidthsMm || [14, 18, 28, 20, 65, 38, 70, 20];
  const totalWidth = colWidthsMm.reduce((sum, w) => sum + w, 0);
  const columnPercents = colWidthsMm.map((w) => (w / totalWidth) * 100);

  return (
    <>
      <style>{inventoryPrintStyles}</style>
      <div className="inventory-list" style={styles.container}>
        {/* Header */}
        <MainPrintLogo companyName={companyName} companyAddress={companyAddress} />

        {/* Document Info */}
        <div style={styles.infoSection}>
          <div style={styles.leftInfo}>
            <p><strong>Date:</strong> {date}</p>
            <p><strong>Loading Port:</strong> {loadingPort}</p>
            {shipperName && <p><strong>Shipper:</strong> {shipperName}</p>}
          </div>
          <div style={styles.rightInfo}>
            <p><strong>Voyage No.:</strong> {voyageNo}</p>
            <p><strong>Vessel:</strong> {shipName}</p>
          </div>
        </div>

        {/* Title */}
        <h2 style={styles.title}>{title}</h2>

        {/* Table */}
        <table className="inventory-table" style={styles.table}>
          <colgroup>
            {columnPercents.map((width, idx) => (
              <col key={idx} style={{ width: `${width}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th style={styles.th}>No.</th>
              <th style={styles.th}>Shipper</th>
              <th style={styles.th}>POD</th>
              <th style={styles.th}>Booking No.</th>
              <th style={styles.th}>Consignee</th>
              <th style={styles.th}>Make / Model</th>
              <th style={styles.th}>Chassis No.</th>
              <th style={styles.th}>Type</th>
              {showCollect && <th style={styles.th}>Collect</th>}
            </tr>
          </thead>
          <tbody>
            {cars.map((car, index) => (
              <React.Fragment key={index}>
                <tr className="table-row">
                  <td style={styles.td}>{car.position}</td>
                  <td style={styles.td}>{car.dealer}</td>
                  <td style={styles.td}>{car.port}</td>
                  <td style={styles.td}>{car.orderNo || ''}</td>
                  <td style={styles.td}>{car.consignee}</td>
                  <td style={styles.td}>{car.make} {car.model}</td>
                  <td style={styles.td}>{car.chassisNo}</td>
                  <td style={styles.td}>{car.type}</td>
                  {showCollect && <td style={styles.td}>{car.collect || ''}</td>}
                </tr>
                {includeRelated && car.relatedCars && car.relatedCars.map((rc, rcIndex) => (
                  <tr key={`${index}-rc-${rcIndex}`} className="table-row" style={{ backgroundColor: printPalette.rowAltBg }}>
                    <td style={{ ...styles.td, borderBottom: rcIndex === car.relatedCars!.length - 1 ? styles.td.borderBottom : 'none' }}></td>
                    <td style={{ ...styles.td, borderBottom: rcIndex === car.relatedCars!.length - 1 ? styles.td.borderBottom : 'none' }}></td>
                    <td style={{ ...styles.td, borderBottom: rcIndex === car.relatedCars!.length - 1 ? styles.td.borderBottom : 'none' }}></td>
                    <td style={{ ...styles.td, borderBottom: rcIndex === car.relatedCars!.length - 1 ? styles.td.borderBottom : 'none' }}></td>
                    <td style={{ ...styles.td, paddingLeft: '12px', fontStyle: 'italic', color: printPalette.primaryDark, borderBottom: rcIndex === car.relatedCars!.length - 1 ? styles.td.borderBottom : 'none' }}>
                      ↳ ({rc.relationshipType})
                    </td>
                    <td style={{ ...styles.td, fontStyle: 'italic', borderBottom: rcIndex === car.relatedCars!.length - 1 ? styles.td.borderBottom : 'none' }}>
                      {rc.make} {rc.model}
                    </td>
                    <td style={{ ...styles.td, fontStyle: 'italic', borderBottom: rcIndex === car.relatedCars!.length - 1 ? styles.td.borderBottom : 'none' }}>{rc.chassisNo}</td>
                    <td style={{ ...styles.td, borderBottom: rcIndex === car.relatedCars!.length - 1 ? styles.td.borderBottom : 'none' }}></td>
                    {showCollect && <td style={{ ...styles.td, borderBottom: rcIndex === car.relatedCars!.length - 1 ? styles.td.borderBottom : 'none' }}></td>}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {/* Total */}
        <div style={styles.total}>
          <strong>Total: {cars.length} vehicles</strong>
        </div>
      </div>
    </>
  );
};

const printStyles = `
  ${sharedPrintStyles}

  @media print {
    .inventory-table { page-break-inside: auto; }
    .inventory-table thead { display: table-header-group; }
    .inventory-table tbody { display: table-row-group; }
    .table-row { page-break-inside: avoid; page-break-after: auto; }
  }
`;

const inventoryPrintStyles = printStyles;

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    ...basePrintContainerStyle,
    padding: '0',
    fontSize: `${printTypography.baseSize}px`,
  },
  infoSection: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    fontSize: `${printTypography.infoSize}px`,
    color: printPalette.text,
  },
  leftInfo: {
    textAlign: 'left',
    color: printPalette.text,
  },
  rightInfo: {
    textAlign: 'right',
    color: printPalette.text,
  },
  title: {
    fontSize: `${printTypography.titleSize}px`,
    fontWeight: 'bold',
    margin: '0 0 15px 0',
    color: printPalette.primaryDark,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: `${printTypography.tableSize}px`,
    color: printPalette.text,
    tableLayout: 'fixed',
  },
  th: {
    borderTop: `2px solid ${printPalette.primaryDark}`,
    borderBottom: `2px solid ${printPalette.primaryDark}`,
    padding: '6px 4px',
    textAlign: 'left',
    fontWeight: 'bold',
    color: printPalette.primaryDark,
    backgroundColor: printPalette.headerBg,
  },
  td: {
    padding: '5px 4px',
    borderBottom: `1px solid ${printPalette.borderLight}`,
    color: printPalette.text,
  },
  total: {
    marginTop: '15px',
    fontSize: '12px',
    color: printPalette.primaryDark,
  },
};

export default InventoryList;

